import i18n from "../i18n";
import React, { useEffect, useMemo, useState } from "react";
import { PageList } from "./pageList";
import { Text, Table } from "@mantine/core";
import _ from "lodash";
import { useCellRender } from "../hooks/useCellRender";
import { useServerApi } from "../hooks/userServerApi";
import { formatLevelProgress, resolveUserLevels } from "../helpers/levelProgress";
import {
  isSystemPostedBy,
  POINT_ORDER_COLUMN_TIP,
  POINT_POSTED_BY_COLUMN_TIP,
  POINT_TXN_COLUMN_TIP,
  POINT_TYPE_COLUMN_TIP,
} from "../helpers/pointTransactionDisplay";
import moment from "moment";

const labelOfPointType = (pointType) => {
  if (!pointType) return "Points";
  if (typeof pointType === "string") return pointType;
  return pointType.name || pointType.code || "Points";
};

export const UserLevelTable = ({ balances = [] }) => {
  const [api] = useServerApi();
  const [cellRender] = useCellRender();
  const [levelSetting, setLevelSetting] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const result = await api.search({
        apiEntity: "setting",
        pageSize: 1,
      });
      if (!cancelled) setLevelSetting(result?.docs?.[0]?.level || null);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const rows = useMemo(() => {
    const pointType = levelSetting?.pointType;
    const pointsPerLevel = levelSetting?.pointsPerLevel;
    if (!pointType || !Number.isFinite(Number(pointsPerLevel)) || Number(pointsPerLevel) <= 0) {
      return [];
    }
    return resolveUserLevels({
      pointBalances: balances,
      levelTypes: [
        {
          name: "Level",
          status: "ACTIVE",
          pointType,
          pointsPerLevel,
        },
      ],
    });
  }, [balances, levelSetting]);

  if (!rows.length) return null;

  return (
    <Table mb="md">
      <thead>
        <tr>
          <th>{i18n.t("Point Type")}</th>
          <th>{i18n.t("Cumulative Points")}</th>
          <th>{i18n.t("Level")}</th>
          <th>{i18n.t("Progress to Next")}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={row.pointType?._id || row.pointType || index}>
            <td>{labelOfPointType(row.pointType)}</td>
            <td>{cellRender.Currency(row.amount || 0, "USD")}</td>
            <td>{row.level}</td>
            <td>{formatLevelProgress(row)}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

export const PointBalanceTable = ({ balances = [] }) => {
  const [cellRender] = useCellRender();
  if (!balances?.length) {
    return (
      <Text size="sm" color="dimmed">
        No promotion points yet
      </Text>
    );
  }
  return (
    <Table>
      <thead>
        <tr>
          <th>{i18n.t("Point Type")}</th>
          <th>{i18n.t("Cumulative Points")}</th>
        </tr>
      </thead>
      <tbody>
        {balances.map((row, index) => (
          <tr key={row.pointType?._id || row.pointType || index}>
            <td>{labelOfPointType(row.pointType)}</td>
            <td>{cellRender.Currency(row.amount || 0, "USD")}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

export const OrderPointHistory = ({ form }) => {
  const [cellRender] = useCellRender();
  const orderId = form?.values?._id;
  if (!orderId) return null;

  const tableSchema = {
    name: "orderPointHistory",
    preQuery: { order: orderId },
    searchableFields: ["code", "type"],
    columns: [
      {
        field: "postedAt",
        headerName: "Posted",
        cellRender: (col, row) =>
          row.postedAt ? moment(row.postedAt).format("YYYY-MM-DD HH:mm") : "-",
      },
      {
        field: "postedBy",
        headerName: "Posted By",
        headerTooltip: POINT_POSTED_BY_COLUMN_TIP,
        cellRender: (col, row) =>
          isSystemPostedBy(row.postedBy) ? "SYSTEM" : cellRender.User(row.postedBy),
      },
      {
        field: "pointType",
        headerName: "Type",
        cellRender: (col, row) =>
          row.pointType?.name || row.pointType?.code || "-",
        headerTooltip: POINT_TYPE_COLUMN_TIP,
      },
      {
        field: "type",
        headerName: "Txn",
        headerTooltip: POINT_TXN_COLUMN_TIP,
      },
      {
        field: "amount",
        headerName: "Points",
        cellRender: (col, row) => cellRender.Currency(row.amount, "USD"),
      },
    ],
  };

  return (
    <PageList
      title=""
      apiEntity="pointTransaction"
      hideSearchBar={true}
      hideActionButton={true}
      showAddButton={false}
      tableSchema={tableSchema}
      formSchema={{}}
      tablePadding={-10}
    />
  );
};

export const UserPointHistory = ({ form, id, pointTypeId }) => {
  const [cellRender] = useCellRender();
  const userId = _.get(form?.values, "_id") ?? id;
  if (!userId) return null;

  const userQuery = {
    $or: [{ user: userId }, { "snapshot.consultantIds": userId }],
  };

  const tableSchema = {
    name: pointTypeId ? `userPointHistory-${pointTypeId}` : "userPointHistory",
    preQuery: pointTypeId
      ? { $and: [userQuery, { pointType: pointTypeId }] }
      : userQuery,
    searchableFields: ["code", "type"],
    columns: [
      {
        field: "postedAt",
        headerName: "Posted",
        cellRender: (col, row) =>
          row.postedAt ? moment(row.postedAt).format("YYYY-MM-DD HH:mm") : "-",
      },
      {
        field: "postedBy",
        headerName: "Posted By",
        headerTooltip: POINT_POSTED_BY_COLUMN_TIP,
        cellRender: (col, row) =>
          isSystemPostedBy(row.postedBy) ? "SYSTEM" : cellRender.User(row.postedBy),
      },
      {
        field: "pointType",
        headerName: "Type",
        cellRender: (col, row) =>
          row.pointType?.name || row.pointType?.code || "-",
        headerTooltip: POINT_TYPE_COLUMN_TIP,
      },
      {
        field: "type",
        headerName: "Txn",
        headerTooltip: POINT_TXN_COLUMN_TIP,
      },
      {
        field: "order",
        headerName: "Order",
        headerTooltip: POINT_ORDER_COLUMN_TIP,
        cellRender: (col, row) => row.order?.code || "-",
      },
      {
        field: "amount",
        headerName: "Points",
        cellRender: (col, row) => {
          const share = (row.snapshot?.consultantShares || []).find(
            (rowShare) => String(rowShare.userId) === String(userId)
          );
          return cellRender.Currency(share?.amount ?? row.amount, "USD");
        },
      },
      { field: "remark", headerName: "Remark" },
    ],
  };

  return (
    <PageList
      title=""
      apiEntity="pointTransaction"
      hideSearchBar={true}
      hideActionButton={true}
      showAddButton={false}
      tableSchema={tableSchema}
      formSchema={{}}
      tablePadding={-10}
    />
  );
};

export const OrderPointPanel = ({ form }) => (
  <>
    <PointBalanceTable balances={form?.values?.pointBalances} />
    <OrderPointHistory form={form} />
  </>
);

export const UserPointPanel = ({ form, id }) => (
  <>
    <UserLevelTable balances={form?.values?.pointBalances} />
    <PointBalanceTable balances={form?.values?.pointBalances} />
    <UserPointHistory form={form} id={id} />
  </>
);

export default OrderPointPanel;
